import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { supabase } from "../lib/supabaseClient"
import { Lock, Mail, Loader2 } from "lucide-react"
import { useLang } from "../context/LangContext"

type OAuthProvider = "google" | "facebook" | "azure"

const T = {
  en: {
    title: "Welcome back",
    sub: "Sign in to access your tools and workspace",
    google: "Continue with Google",
    microsoft: "Continue with Microsoft",
    facebook: "Continue with Facebook",
    or: "or",
    emailLabel: "Email",
    passLabel: "Password",
    emailPH: "you@company.com",
    passPH: "••••••••",
    signIn: "Sign in",
    magic: "Send me a magic link",
    noAccount: "Don't have an account?",
    create: "Create one",
    magicSent: "Check your email for the magic link!",
  },
  es: {
    title: "Bienvenido de vuelta",
    sub: "Inicia sesión para acceder a tus herramientas",
    google: "Continuar con Google",
    microsoft: "Continuar con Microsoft",
    facebook: "Continuar con Facebook",
    or: "o",
    emailLabel: "Correo electrónico",
    passLabel: "Contraseña",
    emailPH: "tu@empresa.com",
    passPH: "••••••••",
    signIn: "Iniciar sesión",
    magic: "Enviarme un enlace mágico",
    noAccount: "¿No tienes cuenta?",
    create: "Crear una",
    magicSent: "¡Revisa tu correo para el enlace mágico!",
  },
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { lang } = useLang()
  const t = T[lang]

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const redirectTo = useMemo(() => `${window.location.origin}/app/dashboard`, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate("/app/dashboard")
    })
  }, [navigate])

  const signInOAuth = async (provider: OAuthProvider) => {
    setLoading(true); setError(null); setMessage(null)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options:
          provider === "azure"
            ? { redirectTo, scopes: "openid profile email User.Read" }
            : { redirectTo },
      })
      if (error) throw error
    } catch (e: any) {
      setError(e?.message ?? "Could not sign in. Please try again.")
      setLoading(false)
    }
  }

  const signInEmailPassword = async () => {
    setLoading(true); setError(null); setMessage(null)
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      navigate("/app/dashboard")
    } catch (e: any) {
      setError(e?.message ?? "Invalid credentials.")
    } finally {
      setLoading(false)
    }
  }

  const signInMagicLink = async () => {
    if (!email) { setError("Enter your email first."); return }
    setLoading(true); setError(null); setMessage(null)
    try {
      const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } })
      if (error) throw error
      setMessage(t.magicSent)
    } catch (e: any) {
      setError(e?.message ?? "Could not send link.")
    } finally {
      setLoading(false)
    }
  }

  const oauthBtn = "w-full rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition disabled:opacity-60 text-gray-800 dark:text-gray-200"

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 p-8 shadow-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="uCloud360" className="h-8 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t.sub}</p>
          </div>

          {/* OAuth */}
          <div className="grid gap-3">
            <button type="button" onClick={() => signInOAuth("google")} disabled={loading} className={oauthBtn}>{t.google}</button>
            <button type="button" onClick={() => signInOAuth("azure")} disabled={loading} className={oauthBtn}>{t.microsoft}</button>
            <button type="button" onClick={() => signInOAuth("facebook")} disabled={loading} className={oauthBtn}>{t.facebook}</button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
            <span className="text-xs text-gray-400">{t.or}</span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
          </div>

          {/* Email/Password */}
          <form onSubmit={(e) => { e.preventDefault(); signInEmailPassword() }} className="grid gap-4">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">{t.emailLabel}</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-black">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.emailPH} className="w-full bg-transparent outline-none text-sm text-black dark:text-white placeholder-gray-400" autoComplete="email" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">{t.passLabel}</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-black">
                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t.passPH} className="w-full bg-transparent outline-none text-sm text-black dark:text-white placeholder-gray-400" autoComplete="current-password" />
              </div>
            </div>

            {error && <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
            {message && <div className="rounded-xl border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30 p-3 text-sm text-green-700 dark:text-green-300">{message}</div>}

            <button type="submit" disabled={loading} className="w-full rounded-xl bg-[#2563EB] text-white hover:bg-[#1d4ed8] px-4 py-3 text-sm font-semibold transition disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.signIn}
            </button>

            <button type="button" onClick={signInMagicLink} disabled={loading} className="w-full rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition disabled:opacity-60 text-gray-700 dark:text-gray-300">
              {t.magic}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t.noAccount}{" "}
            <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:underline">{t.create}</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
